import { QI_FIELDS, QI_IDENTIFIER_COLUMNS, QI_TEMPLATE_SHA256 } from '../manifest';
import { formatQiXlsxDate, validateQiValues } from '../validate';
import { qiCommentRequired } from './comments';
import headers from './gpms-columns.json';
import requiredness from './gpms-required-fields.json';
import type { QiExportCell, QiExportExclusion, QiLockedExportVersion } from './types';

export const QI_GPMS_HEADERS: readonly string[] = Object.freeze(headers);
export const QI_GPMS_REQUIREDNESS = requiredness;
export const QI_GPMS_HEADER_LABELS: readonly string[] = Object.freeze([
  ...QI_IDENTIFIER_COLUMNS.map((column) => column.label),
  ...QI_FIELDS.map((field) => field.label),
]);
export function qiReportingPeriodName(financialYear: number, quarter: number): string {
  if (
    !Number.isInteger(financialYear) ||
    financialYear < 2000 ||
    financialYear > 9998 ||
    ![1, 2, 3, 4].includes(quarter)
  )
    throw new Error('qi_reporting_period_invalid');
  return `Quarter ${quarter} FY ${String(financialYear % 100).padStart(2, '0')} - ${String((financialYear + 1) % 100).padStart(2, '0')}`;
}

export function buildQiGpmsRows(versions: readonly QiLockedExportVersion[]) {
  if (
    headers.length !== 180 ||
    QI_FIELDS.length !== 178 ||
    QI_FIELDS.some(
      (field, index) => field.column !== index + 3 || headers[index + 2] !== field.code,
    )
  )
    throw new Error('qi_template_column_mismatch');
  if (requiredness.templateSha256 !== QI_TEMPLATE_SHA256)
    throw new Error('qi_template_pin_mismatch');
  const rows: QiExportCell[][] = [];
  const includedVersionIds: string[] = [];
  const excluded: QiExportExclusion[] = [];
  const homes = new Set<string>();
  let reportingPeriod: string | undefined;
  for (const version of versions) {
    const period = version.inputs.period;
    const periodName = qiReportingPeriodName(period.financial_year, period.quarter);
    if (reportingPeriod && periodName !== reportingPeriod)
      throw new Error('qi_upload_same_quarter_required');
    reportingPeriod = periodName;
    if (homes.has(version.facility_id)) throw new Error('qi_upload_duplicate_home');
    homes.add(version.facility_id);
    const profile = version.inputs.home_profile;
    if (
      !profile ||
      profile.programme !== 'RESIDENTIAL' ||
      !profile.active ||
      !/^\d{1,20}$/.test(profile.service_identifier)
    ) {
      excluded.push({
        versionId: version.id,
        periodId: version.period_id,
        reason: 'home_profile_missing',
        fields: [],
      });
      continue;
    }
    const invalid = validateQiValues(version.manifest_values).filter(
      (issue) => issue.category !== 'POLICY',
    );
    if (invalid.length) {
      excluded.push({
        versionId: version.id,
        periodId: version.period_id,
        reason: 'invalid_values',
        fields: invalid.map((issue) => issue.path),
      });
      continue;
    }
    const missing = requiredness.uploadRequiredFields.filter((code) => {
      const value = version.manifest_values[code];
      // Comments may be blank unless the Manual requires them for a recorded exclusion.
      if (QI_FIELDS.find((f) => f.code === code)?.kind === 'comment')
        return (
          qiCommentRequired(code, version.manifest_values) &&
          (value?.state !== 'measured' || typeof value.value !== 'string' || !value.value.trim())
        );
      return value?.state !== 'measured';
    });
    if (missing.length) {
      excluded.push({
        versionId: version.id,
        periodId: version.period_id,
        reason: 'required_fields_not_collected',
        fields: missing,
      });
      continue;
    }
    const identifier = Number(profile.service_identifier);
    // Whole-number U cells cannot safely represent leading-zero identifiers or
    // integers beyond Excel's 15 significant digits. Never silently round/strip them.
    if (
      profile.service_identifier.length > 15 ||
      !Number.isSafeInteger(identifier) ||
      String(identifier) !== profile.service_identifier
    ) {
      excluded.push({
        versionId: version.id,
        periodId: version.period_id,
        reason: 'identifier_not_excel_safe',
        fields: ['NAPS Service ID'],
      });
      continue;
    }
    const row: QiExportCell[] = [identifier, periodName];
    for (const field of QI_FIELDS) {
      const value = version.manifest_values[field.code];
      row.push(
        value?.state !== 'measured'
          ? null
          : field.kind === 'date'
            ? formatQiXlsxDate(String(value.value))
            : value.value,
      );
    }
    rows.push(row);
    includedVersionIds.push(version.id);
  }
  return {
    headers: QI_GPMS_HEADERS,
    headerLabels: QI_GPMS_HEADER_LABELS,
    rows,
    includedVersionIds,
    excluded,
  };
}

/** Structural adapter for the repository's existing ExcelJS. No new utils dependency.
 * Statura workbook: format headers only, no Department template asset or layout.
 */
export interface QiWorkbookWriter {
  addWorksheet(name: string): { addRow(values: QiExportCell[]): unknown };
  xlsx: { writeBuffer(): Promise<ArrayBuffer | Uint8Array> };
}
export async function buildQiGpmsWorkbook(
  versions: readonly QiLockedExportVersion[],
  workbook: QiWorkbookWriter,
) {
  const result = buildQiGpmsRows(versions);
  if (!result.rows.length) return { ...result, bytes: null };
  const sheet = workbook.addWorksheet(requiredness.sheet);
  sheet.addRow([...result.headers]);
  // The U format has two header rows: codes then descriptive column headers.
  // Both are regenerated from the manifest; no styles, layout or asset copied.
  sheet.addRow([...result.headerLabels]);
  for (const row of result.rows) sheet.addRow(row);
  return { ...result, bytes: new Uint8Array(await workbook.xlsx.writeBuffer()) };
}
